import { css } from 'aphrodite';
import React from 'react';
import * as Api from '@ViewModels';
import { useLocalStorage } from '../../../../../hooks/useLocalStorage';
import { useAccountProfileQuery } from '../../../../../queries/AccountProfile/useAccountProfileQuery';
import { hasContentPreferences } from '../../../../../utils/hasContentPreferences';
import { AiButton } from '../../../../components-v2/AiButton';
import { Button } from '../../../../components-v2/Button';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Checkbox } from '../../../../components/Checkbox';
import { Modal } from '../../../../components/Modal';
import { AIStarsIcon } from '../../../../components/svgs/icons/AIStars';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { styleSheet } from './styles';

const LOCAL_STORAGE_DONT_SHOW_AGAIN_KEY = 'multi-scheduler-ai-modal-dont-show-again';

export const MultiSchedulerAiModal = ({
	onGenerateContentClicked,
	impersonationContext,
	hasEligibleBulkAiSuggestion,
	isCustomizeBlockedByConcurrentWork = false,
}: {
	onGenerateContentClicked: () => void;
	impersonationContext?: Api.IImpersonationContext;
	hasEligibleBulkAiSuggestion: boolean;
	isCustomizeBlockedByConcurrentWork?: boolean;
}) => {
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
	const [isOpen, setIsOpen] = React.useState(true);
	const [persistedDontShow, setPersistedDontShow] = useLocalStorage({
		key: LOCAL_STORAGE_DONT_SHOW_AGAIN_KEY,
		initialValue: false,
	});

	const [dontShowAgain, setDontShowAgain] = React.useState(false);

	React.useEffect(() => {
		if (!hasEligibleBulkAiSuggestion) {
			setDontShowAgain(false);
		}
	}, [hasEligibleBulkAiSuggestion]);

	const closeModal = () => {
		setIsOpen(false);
		if (dontShowAgain) {
			setPersistedDontShow(true);
		}
	};

	if (persistedDontShow) {
		return null;
	}

	const isAdminImpersonating = !!impersonationContext?.account?.id;
	if (isAdminImpersonating) {
		return null;
	}

	if (!hasContentPreferences(accountProfileQuery.data)) {
		return null;
	}

	return (
		<Modal isOpen={isOpen}>
			<div className={css(styleSheet.header)}>
				<button onClick={() => setIsOpen(false)} aria-label='Close modal'>
					<XIcon height={12} width={12} color={THEME.colors.text.light} />
				</button>
			</div>

			<div className={css(styleSheet.iconContainer)}>
				<AIStarsIcon
					height={100}
					width={84}
					stopColorOne={THEME.colors.primary.main}
					stopColorTwo={THEME.colors.primary.main}
				/>
			</div>

			<div className={css(styleSheet.content)}>
				<p className={css(styleSheet.aiButtonContainer)}>
					<span className={css(styleSheet.aiButtonText)}>
						<span className={css(styleSheet.newTag)}>New!</span> Try Customizing this content with AI
					</span>
				</p>

				<p className={css(styleSheet.contentText)}>
					You can now use AI to turn your content preferences
					<br /> into campaigns unique to your business.
				</p>

				<div className={css(styleSheet.actionsContainer)}>
					<AiButton
						title='Customize Now'
						disabled={!hasEligibleBulkAiSuggestion || isCustomizeBlockedByConcurrentWork}
						onClick={() => {
							onGenerateContentClicked();
							closeModal();
						}}
					/>
					<Button variant='secondary' onClick={closeModal}>
						Close
					</Button>
				</div>

				<Checkbox
					checked={dontShowAgain}
					disabled={!hasEligibleBulkAiSuggestion}
					id='content-calendar-ai-modal-dont-show-again'
					onChange={() => setDontShowAgain(!dontShowAgain)}
				>
					<span>Don&apos;t show this screen again</span>
				</Checkbox>
			</div>
		</Modal>
	);
};
